import { useCallback, useRef } from 'react';
import { fabric } from 'fabric';
import { emitDeleteObject } from '../../../socket';
import { isOffline } from '../../../utils/helper';
import eraserCursor from '../../../assets/eraser.svg';

/**
 * The eraser tool: while active, a mouse-down on the canvas fades and removes
 * every object under the pointer. `start` arms it, `stop` disarms it.
 */
const useEraser = (canvasRef, saveStateToUndoStack) => {
    const handlerRef = useRef(null);

    const stop = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.isErasing = false;
        if (handlerRef.current) canvas.off('mouse:down', handlerRef.current);
        handlerRef.current = null;
    }, [canvasRef]);

    const start = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        stop();
        canvas.isErasing = true;
        canvas.isDrawingMode = false;
        canvas.setCursor(`url(${eraserCursor}), auto`);

        const erase = (event) => {
            if (!canvas.isErasing) return;
            const { x, y } = event.pointer;
            const eraserPath = new fabric.Rect({
                left: x, top: y, width: 2, height: 5,
                originX: 'center', originY: 'center',
                selectable: false, evented: false,
                fill: new fabric.Pattern({ source: eraserCursor, repeat: 'no-repeat' }),
            });
            canvas.discardActiveObject();
            canvas.forEachObject((object) => {
                if (!eraserPath.intersectsWithObject(object)) return;
                // Fade first so the removal reads as deliberate.
                object.set('opacity', 0.5);
                canvas.renderAll();
                setTimeout(() => {
                    canvas.remove(object);
                    !isOffline() && emitDeleteObject(object.id);
                }, 300);
                saveStateToUndoStack();
            });
            canvas.renderAll();
            return false;
        };
        handlerRef.current = erase;
        canvas.on('mouse:down', erase);
    }, [canvasRef, saveStateToUndoStack, stop]);

    return { start, stop };
};

export default useEraser;
